import { useSyncExternalStore } from 'react';

/**
 * Хранилище тостов — вне React намеренно.
 *
 * Тост почти никогда не появляется из разметки: его показывает обработчик
 * мутации, перехватчик ошибок API или подписка на события сервера. Всё это
 * код без доступа к контексту, поэтому очередь живёт в модуле, а React лишь
 * подписывается на неё через `useSyncExternalStore`.
 */

export type ToastTone = 'success' | 'error' | 'warning' | 'info' | 'loading';

export interface ToastActionSpec {
  label: string;
  onClick: () => void;
  /**
   * Как то же самое сделать без тоста — для скринридера.
   *
   * Тост исчезает по таймеру, а человек, слушающий страницу, доходит до
   * кнопки уже после. Поэтому Radix требует словами описать запасной путь:
   * «Восстановить сайт можно в разделе Корзина».
   */
  altText?: string;
}

export interface ToastOptions {
  description?: string;
  action?: ToastActionSpec;
  /** Своё время жизни в миллисекундах; `null` — не закрывать самому. */
  duration?: number | null;
  /**
   * Свой идентификатор. Повторный вызов с тем же id обновляет тост на месте,
   * а не добавляет второй такой же: пять одинаковых «Не удалось сохранить»
   * от пяти нажатий подряд — это шум, а не пять новостей.
   */
  id?: string;
}

export interface ToastRecord {
  id: string;
  tone: ToastTone;
  title: string;
  description?: string;
  action?: ToastActionSpec;
  /** `Infinity` — закрывается только руками. */
  duration: number;
  /** Закрывающийся тост остаётся в списке, пока идёт анимация ухода. */
  open: boolean;
}

export type ToastPatch = Partial<Omit<ToastRecord, 'id' | 'open' | 'duration'>> & {
  duration?: number | null;
};

/**
 * Сколько времени тост живёт сам.
 *
 * Успех сообщает о том, что и так произошло на глазах, — ему хватает
 * четырёх секунд. Ошибка не закрывается вообще: пропущенное сообщение об
 * ошибке означает, что человек ушёл с экрана в уверенности, будто изменения
 * сохранились. Это дороже, чем лишний крестик.
 */
const LIFETIME: Record<ToastTone, number> = {
  success: 4000,
  info: 4000,
  warning: 6000,
  error: Number.POSITIVE_INFINITY,
  loading: Number.POSITIVE_INFINITY,
};

/** Тосту с кнопкой нужно больше времени: «Отменить» надо успеть нажать. */
const WITH_ACTION_MIN_MS = 8000;

/**
 * Сколько тостов держим на экране разом.
 *
 * Четвёртый закрывает первый, а не пристраивается снизу: столбик уведомлений
 * во весь экран перекрывает ту самую таблицу, ради которой всё и делалось.
 */
const MAX_VISIBLE = 3;

/** Запас на анимацию ухода: пока она идёт, запись ещё нужна. */
const EXIT_GRACE_MS = 260;

const EMPTY: readonly ToastRecord[] = [];

let records: readonly ToastRecord[] = EMPTY;
let counter = 0;

const listeners = new Set<() => void>();
const removalTimers = new Map<string, number>();

function emit(): void {
  for (const listener of listeners) {
    listener();
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): readonly ToastRecord[] {
  return records;
}

function getServerSnapshot(): readonly ToastRecord[] {
  return EMPTY;
}

/** Подписка на очередь тостов. Нужна ровно одному компоненту — `Toaster`. */
export function useToasts(): readonly ToastRecord[] {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

function resolveDuration(
  tone: ToastTone,
  duration: number | null | undefined,
  action: ToastActionSpec | undefined,
): number {
  if (duration === null) {
    return Number.POSITIVE_INFINITY;
  }

  if (duration !== undefined) {
    return duration;
  }

  const base = LIFETIME[tone];

  return action === undefined ? base : Math.max(base, WITH_ACTION_MIN_MS);
}

function removeToast(id: string): void {
  removalTimers.delete(id);

  const next = records.filter((record) => record.id !== id);

  if (next.length === records.length) {
    return;
  }

  records = next;
  emit();
}

function closeToast(id: string): void {
  let changed = false;

  const next = records.map((record) => {
    if (record.id !== id || !record.open) {
      return record;
    }

    changed = true;

    return { ...record, open: false };
  });

  if (!changed) {
    return;
  }

  records = next;

  // Запись доживает до конца анимации ухода. Убрать её сразу — значит
  // выдернуть узел из разметки посреди движения, и тост исчезнет рывком.
  window.clearTimeout(removalTimers.get(id));
  removalTimers.set(
    id,
    window.setTimeout(() => {
      removeToast(id);
    }, EXIT_GRACE_MS),
  );

  emit();
}

function push(tone: ToastTone, title: string, options: ToastOptions = {}): string {
  const { description, action, duration, id } = options;

  counter += 1;

  const record: ToastRecord = {
    id: id ?? `toast-${String(counter)}`,
    tone,
    title,
    duration: resolveDuration(tone, duration, action),
    open: true,
    ...(description !== undefined && { description }),
    ...(action !== undefined && { action }),
  };

  const known = records.some((item) => item.id === record.id);

  records = known
    ? records.map((item) => (item.id === record.id ? record : item))
    : [...records, record];

  if (known) {
    window.clearTimeout(removalTimers.get(record.id));
    removalTimers.delete(record.id);
  }

  emit();

  const shown = records.filter((item) => item.open);

  for (const extra of shown.slice(0, Math.max(0, shown.length - MAX_VISIBLE))) {
    closeToast(extra.id);
  }

  return record.id;
}

/**
 * Показать тост.
 *
 * `loading` вместе с `update` покрывает долгие действия: публикацию сайта,
 * загрузку медиа. Отдельного «тоста-обещания» нет намеренно — мутации
 * TanStack Query сообщают о себе колбэками, а не промисом, и обёртка над
 * промисом здесь оказалась бы лишним слоем.
 */
export const toast = {
  success: (title: string, options?: ToastOptions) => push('success', title, options),
  error: (title: string, options?: ToastOptions) => push('error', title, options),
  warning: (title: string, options?: ToastOptions) => push('warning', title, options),
  info: (title: string, options?: ToastOptions) => push('info', title, options),
  loading: (title: string, options?: ToastOptions) => push('loading', title, options),

  /**
   * Изменить показанный тост — «Публикуем…» превращается в «Опубликовано».
   *
   * Смена тона без явного времени пересчитывает и время жизни: иначе бывший
   * `loading` остался бы с бесконечной длительностью и висел на экране уже
   * успехом, пока его не закроют руками.
   */
  update: (id: string, patch: ToastPatch): void => {
    const { duration, ...rest } = patch;

    records = records.map((record) => {
      if (record.id !== id) {
        return record;
      }

      const tone = rest.tone ?? record.tone;
      const action = 'action' in rest ? rest.action : record.action;

      return {
        ...record,
        ...rest,
        duration:
          duration === undefined && rest.tone === undefined
            ? record.duration
            : resolveDuration(tone, duration, action),
      };
    });

    emit();
  },

  /** Закрыть один тост или, без аргумента, все сразу. */
  dismiss: (id?: string): void => {
    if (id === undefined) {
      for (const record of records) {
        closeToast(record.id);
      }

      return;
    }

    closeToast(id);
  },
};

/** Мгновенно очистить очередь. Нужно тестам, чтобы соседние не влияли друг на друга. */
export function resetToasts(): void {
  for (const timer of removalTimers.values()) {
    window.clearTimeout(timer);
  }

  removalTimers.clear();
  records = EMPTY;
  counter = 0;
  emit();
}
